/**
 * FP3-030 — Forgot password page.
 *
 * Public page. Submits the email; backend returns a generic response
 * for both known and unknown emails so the endpoint cannot be used
 * as an account-existence oracle. We render the same calm message
 * in both cases.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.forgotPassword({ email: email.trim().toLowerCase() });
    } catch {
      // Backend always returns 200 with a generic message; treat any
      // network blip as success to preserve the no-oracle property.
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="min-h-screen bg-[#F7F5EF] body-font flex items-center justify-center px-5 py-10"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-7">
          <div className="w-10 h-10 rounded-xl bg-[#1E3F33] flex items-center justify-center shadow-sm">
            <span className="text-white text-base font-bold heading-font">F</span>
          </div>
          <div className="leading-tight">
            <div className="text-[17px] font-bold text-[#1E3F33] heading-font tracking-tight">
              Frame<span className="text-[#8FA998] font-medium ml-0.5">Practice</span>
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA998] mt-0.5">
              Provider workspace
            </div>
          </div>
        </div>

        <Card
          className="border-[#E5E3DB] shadow-[0_2px_14px_rgba(31,41,55,0.04)] rounded-2xl"
        >
          <CardContent className="p-7 sm:p-8">
            <div
              data-testid="forgot-password-pill"
              className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-[#1E3F33] bg-[#E8F0EB] rounded-full px-2.5 py-1 mb-3 font-semibold"
            >
              <ShieldCheck size={11} weight="duotone" />
              Account recovery
            </div>
            {submitted ? (
              <div data-testid="forgot-password-confirmation">
                <div className="w-12 h-12 mb-4 rounded-full bg-[#E8F0EB] flex items-center justify-center">
                  <CheckCircle size={26} weight="duotone" className="text-[#1E3F33]" />
                </div>
                <h1 className="text-[22px] font-semibold text-[#1F2937] heading-font leading-tight">
                  Check your email
                </h1>
                <p className="text-[13.5px] text-[#4B5563] leading-relaxed mt-3">
                  If an active Frame Practice account exists for this email,
                  a reset link will be sent.
                </p>
                <p className="text-[12px] text-[#6B7280] leading-relaxed mt-2">
                  The link is single-use and expires in one hour.
                </p>
                <Link
                  to="/login"
                  data-testid="forgot-back-to-login"
                  className="inline-block mt-6 text-[12.5px] text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
                >
                  ← Back to sign in
                </Link>
              </div>
            ) : (
              <>
                <h1 className="text-[24px] font-semibold text-[#1F2937] heading-font leading-tight">
                  Forgot your password?
                </h1>
                <p className="text-[12.5px] text-[#6B7280] mt-1.5">
                  Enter the email on your Frame Practice account and we'll send
                  a reset link.
                </p>
                <form
                  data-testid="forgot-password-form"
                  onSubmit={onSubmit}
                  className="space-y-4 mt-6"
                  noValidate
                >
                  <div>
                    <Label
                      htmlFor="fp-email"
                      className="text-[11.5px] uppercase tracking-[0.1em] text-[#6B7280] font-semibold"
                    >
                      Email
                    </Label>
                    <Input
                      id="fp-email"
                      data-testid="forgot-password-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="mt-1.5 h-10 border-[#E5E3DB] bg-white focus-visible:ring-[#1E3F33]/30 focus-visible:border-[#1E3F33]"
                      placeholder="you@school.org"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="forgot-password-submit"
                    disabled={submitting}
                    className="w-full h-10 bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white rounded-lg text-sm font-medium tracking-tight transition-colors"
                  >
                    {submitting ? "Sending…" : "Send reset link"}
                  </Button>
                </form>
                <p className="mt-5 text-[11.5px] text-[#6B7280]">
                  <Link
                    to="/login"
                    className="text-[#1E3F33] font-medium hover:text-[#173127] underline decoration-[#8FA998]/60 underline-offset-[3px] decoration-1 hover:decoration-[#1E3F33] transition-colors"
                  >
                    ← Back to sign in
                  </Link>
                </p>
              </>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-[11px] text-[#9AA0A6] mt-5">
          © Frame Practice · invite-only provider workspace
        </p>
      </div>
    </div>
  );
}
